import { useEffect } from "react";
import { Outlet, ScrollRestoration } from "react-router";
import AppShellContent from "./AppShellContent";
import DelegatedRouteGuard from "@/components/delegation/DelegatedRouteGuard";
import { getUserInfo } from "@/api/user";
import { getStamdataPerson } from "@/api/stamdata";
import { redirectToLogin } from "@/routerUtil";
import { HomeLoaderResponse } from "@/interfaces/loaders";
import { UserInfo } from "@/interfaces/authentication";
import { PersonStamdata } from "@/interfaces/stamdata";
import SignOutWarningModal from "@/components/Modal/SignOutWarningModal";
import { API_LINKS } from "@/constants/api";
import SmallScreenWarningModal from "@/components/Modal/SmallScreenWarningModal";
import { UNAUTHORIZED_ROUTES } from "@/constants/routes";
import { canAccessDelegatedPath } from "@/lib/delegationRouteAccess";

export const homeLoader = async (): Promise<Response | HomeLoaderResponse> => {
  const url = new URL(window.location.href);
  const currentPath = url.pathname;
  const unauthorizedRoutePaths = Object.values(UNAUTHORIZED_ROUTES).map(
    (route) => route.pathName,
  );

  if (unauthorizedRoutePaths.includes(currentPath)) {
    return {} as any;
  }

  let userInfo: UserInfo | null;

  try {
    userInfo = await getUserInfo();
  } catch (_error: any) {
    const shouldNotIncludeReturnUrl = url.searchParams.has("link");
    return currentPath.includes("LogoutDone")
      ? ({} as any)
      : redirectToLogin(shouldNotIncludeReturnUrl);
  }

  let stamdata: PersonStamdata | null = null;
  // De to grunde til at stamdata mangler holdes adskilt: profilen findes ikke i portalen endnu
  // (ADV-288), eller forespørgslen fejlede. Kun den sidste bliver bedre af at genindlæse siden, så
  // siderne skal kunne skrive det rigtige til medlemmet.
  let profilIkkeKlar = false;
  if (userInfo?.id) {
    try {
      stamdata = await getStamdataPerson(userInfo.id);
      profilIkkeKlar = stamdata === null;
    } catch (error) {
      console.warn("Kunne ikke hente stamdata:", error);
    }
  }

  if (userInfo && !canAccessDelegatedPath(currentPath, userInfo)) {
    return {
      userInfo,
      stamdata,
      profilIkkeKlar,
      delegatedAccessDenied: true,
    };
  }

  return {
    userInfo,
    stamdata,
    profilIkkeKlar,
  };
};

export default function AppShell() {
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !document.cookie.match("AuthToken")) {
        window.location.href = API_LINKS.baseUrl;
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <>
      <ScrollRestoration />
      <AppShellContent>
        <DelegatedRouteGuard>
          <Outlet />
        </DelegatedRouteGuard>
      </AppShellContent>
      <SignOutWarningModal />
      <SmallScreenWarningModal />
    </>
  );
}
